import { getElementPositionOnCanvas, getRandomFloatInRange } from "./Global";
import { Vector2, Vector3 } from "./geometry";

/**
 * Represents an entity with a collection of components.
 */
class Entity {
    /**
     * 
     * @param {number} id Entity's ID
     * @param {EntityManager} manager Entity's database
     */
    constructor(id, manager) {
        /** @type {Map<string, Object>} */
        this.components = new Map();
        /** @type {number} */
        this.id = id;
        /** @type {EntityManager} */
        this.manager = manager;
    }

    /**
    * Adds a component to the entity.
    * @param {Object} component - The component to add.
    */
    addComponent(component) {
        const componentName = component.constructor.name;
        if (this.components.has(componentName)) {
            console.warn(`Component ${componentName} already exists and will not be added again.`);
            return;
        }
        this.components.set(componentName, component);
        component.entity = this;
        if (this.manager !== undefined)
            this.manager.entityChanged(this.id);
    }

    /**
    * Removes a component from the entity.
    * @param {Object} component - The component to remove.
    * @returns {boolean} - True if component was found and removed. False otherwise.
    */
    removeComponent(component) {
        if (this.components.has(component.name))
            this.components.get(component.name).entity = null;

        let res = this.components.delete(component.name);
        if (res && this.manager !== undefined)
            this.manager.entityChanged(this.id);
        return res;
    }

    /**
    * Gets a component of the specified class from the entity.
    * @template T
    * @param {new (...args: any[]) => T} componentClass - The class of the component to get.
    * @returns {T | undefined} - The component instance, or undefined if not found.
    */
    getComponent(componentClass) {
        return this.components.get(componentClass.name);
    }

    /**
    * Removes all components ad marks entity for deletion
    */
    Clear() {
        this.components.clear();  
        if (this.manager !== undefined)
            this.manager.entityChanged(this.id);
    }
}

class EntityManager {
    constructor() {
        /** @type {Map<number, Entity>} */
        this.entities = new Map();
        /** @type {number} */
        this.idCounter = 0;

        /** @type {[Function]} */
        this.entityChangeListeners = [];
    }
    onEntityChanged(listener){
        this.entityChangeListeners.push(listener);
    }
    entityChanged (id){
        this.entityChangeListeners.forEach(listener => listener(this.entities.get(id)));
    }
    /**
    * Adds an entity to pool.
    * @param {Entity} entity - entity to add.
    */
    addEntity(entity) {
        entity.id = this.idCounter;
        entity.manager = this;
        this.entities.set(this.idCounter, entity);
        this.entityChanged(entity.id);
        this.idCounter++;
    }
  
    /**
    * Removes an entity from pool.
    * @param {Entity} entity - entity to add.
    * @returns {boolean} True if deleted successfully
    */
    deleteEntity(entity) {
        if (!entity){
            console.error(`trying to delete entity, which is undefined`);
        }
        entity.Clear();
        return this.entities.delete(entity.id);
    }
  

    forEach(callback) {
        this.entities.forEach((entity, id) => {
            callback(entity);
        });
    }

    every(callback) {
        this.entities.forEach((entity, id) => {
            if (!callback(entity))
                return false;
        });
        return true;
    }

    /**
     * gets all entities in circle area
     * @param {Vector2} center 
     * @param {number} radius 
     * @returns {Map<number, Entity>}
     */
    circle(center, radius){
        let result = new Map();
        this.entities.forEach((entity, id) => {
            if (Vector2.Distance(entity.getComponent(TransformComponent).position, center) <= radius){
                result.set(id, entity);
            }
        });
        return result;
    }

    clear(){
        this.forEach((entity) => {
            this.deleteEntity(entity);
        });
        this.entityChangeListeners = [];
        this.idCounter = 0;
    }

    /**
     * Finds entities that have all the specified components.
     * @param {...new (...args: any[]) => any} components - The classes of the components to check for.
     * @returns {Map<number, any[] | any>} - A Map containing arrays of the requested components, keyed by entity ID, or a single component if only one type is requested.
     */
    findComponentsCache(...components) {
        /** @type {Map<number, any[] | any>} */
        const result = new Map();
        const singleComponentRequest = components.length === 1;

        this.forEach((entity) => {
            // Collect all the required components
            const collectedComponents = components.map(component => entity.getComponent(component));

            // Check if all components were found (none are undefined)
            if (collectedComponents.every(component => component !== undefined)) {
                if (singleComponentRequest) {
                    result.set(entity.id, collectedComponents[0]);
                } else {
                    result.set(entity.id, collectedComponents);
                }
            }
        });

        return result;
    }



    /**
     * Finds entities that have all the specified components.
     * @param {...new (...args: any[]) => any} components - The classes of the components to check for.
     * @returns {Map<number, Entity>} - A Map containing entities that have all the specified components, keyed by entity ID.
     */
    findComponents(...components) {
        /** @type {Map<number, Entity>} */
        let result = new Map();

        this.forEach((entity) => {
            // Check if the entity has all the required components
            const hasAllComponents = components.every(component => entity.getComponent(component) !== undefined);
            
            // If the entity has all the required components, add it to the result map
            if (hasAllComponents) {
                result.set(entity.id, entity);
            }
        });

        return result;
    }
  
}

class QuadNode{
    /**
     * 
     * @param {number} x 
     * @param {number} y 
     * @param {number} width 
     * @param {number} height 
     * @param {number} centerX 
     * @param {number} centerY 
     * @param {number} magnitude 
     */
    constructor(x, y, width, height, centerX, centerY, magnitude) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.centerX = centerX;
        this.centerY = centerY;
        this.magnitude = magnitude;
        this.empty = true;     // <-- new flag: true means no data yet
        /** @type {[QuadNode]} */
        this.sub = [null, null, null, null];
        this.isLeaf = true;
    }
}
  



class TransformComponent {
    /**
     * @param {Vector2} position - entity position
     * @param {boolean} wrapping - if true, entity will wrap around canvas edges
     */
    constructor(position, wrapping=true) {
        /** @type {Vector2} */
        this.position = position;
        /** @type {boolean} */
        this.wrapping = wrapping;
        /** @type {boolean} */
        this.enabled = true;

        this.entity = null;
    }
}

class RigidbodyComponent {
    /**
     * @param {TransformComponent} transform - entity transform
     * @param {Vector2} velocity - entity velocity
     * @param {number} mass - entity mass
     * @param {number} damping - entity velocity damping
     * @param {boolean} gravitatable - if true, gravity is applied to object
     */
    constructor(transform, velocity, mass, damping=0, gravitatable=true) {
        /** @type {TransformComponent} */
        this.transform = transform;
        /** @type {Vector2} */
        this.velocity = velocity;
        /** @type {number} */
        this.mass = mass;
        /** @type {number} */
        this.damping = damping;
        /** @type {boolean} */
        this.gravitatable = gravitatable;

        /** @type {Vector2} */
        this.accumulatedForce = new Vector2(0, 0);
        /** @type {Vector2} */
        this.accumulatedImpulse = new Vector2(0, 0);
        /** @type {boolean} */
        this.enabled = true;

        this.entity = null;
    }
    /**
     * adds force to physic object
     * @param {Vector2} force force vector
     * @param {number} forceMode 0 - force, 1 - impulse
     */
    AddForce (force, forceMode = 0){
        if (forceMode === 0){
            this.accumulatedForce.x += force.x;
            this.accumulatedForce.y += force.y;
        }
        if (forceMode === 1){
            this.accumulatedImpulse.x += force.x;
            this.accumulatedImpulse.y += force.y;
        }
    }
}

class RigidbodyFixPosition {
    /**
     * @param {Vector2} targetPosition - position to stick to
     * @param {number} density - the magnitude of correction force
     */
    constructor(targetPosition, density, enabled = true) {
        /** @type {Vector2} */
        this.targetPosition = targetPosition;
        /** @type {number} */
        this.density = density;
        /** @type {boolean} */
        this.enabled = enabled;

        this.entity = null;
    }
}

class GravityComponent {
    /**
     * @param {number} magnitude - gravity magnitude
     * @param {boolean} enabled - if enabled
     */
    constructor(magnitude, enabled = true) {
        /** @type {number} */
        this.magnitude = magnitude;
        /** @type {boolean} */
        this.enabled = enabled;

        this.entity = null;
    }
}

class CircleRendererComponent{
    /**
     * @param {Vector3} color - circle color
     * @param {number} radius - circle radius
     * @param {boolean} enabled - if enabled
     */
    constructor(color, radius, enabled = true) {
        /** @type {Vector3} */
        this.color = color;
        /** @type {number} */
        this.radius = radius;
        /** @type {Vector3 | null} */
        this.targetColor = null;
        /** @type {number} */
        this.transitionSpeed = 0;
        /** @type {number} */
        this.decayTime = -1;
        /** @type {number} */
        this.totalDecayTime = -1;
        /** @type {boolean} */
        this.enabled = enabled;

        this.entity = null;
    }
    SetColor(targetColor, time){
        this.targetColor = targetColor;
        /** @type {number} */
        this.transitionSpeed = (Vector3.dist(this.targetColor, this.color)) / time;
    }
    SetDecay(totalDecayTime){
        this.totalDecayTime = totalDecayTime;
        this.decayTime = totalDecayTime;
    }
}

class RoundRecRendererComponent{
    /**
     * @param {Vector3} color - entity color
     * @param {number} width - rectangle width
     * @param {number} height - rectangle height
     * @param {number} roundRadius - rectangle rounding radius
     * @param {boolean} enabled - if enabled
     */
    constructor(color, width, height, roundRadius, enabled = true) {
        /** @type {Vector3} */
        this.color = color;
        /** @type {number} */
        this.roundRadius = roundRadius;
        /** @type {number} */
        this.width = width;
        /** @type {number} */
        this.height = height;
        /** @type {Vector3 | null} */
        this.targetColor = null;
        /** @type {number} */
        this.transitionSpeed = 0;
        /** @type {boolean} */
        this.enabled = enabled;

        this.entity = null;
    }
    SetColor(targetColor, time){
        this.targetColor = targetColor;
        /** @type {number} */
        this.transitionSpeed = (Vector3.dist(this.targetColor, this.color)) / time;
    }
}

class SpiralAnimationComponent{
    /**
     * @param {number} radius - circle radius
     * @param {Vector2} targetPosition - target pos
     * @param {number} mxTime - animation duration
     * @param {Function} callback - animation duration
     * @param {boolean} enabled - if enabled
     */
    constructor(radius, targetPosition, mxTime, callback, enabled = true) {
        /** @type {number} */
        this.radius = radius;
        /** @type {Vector2} */
        this.targetPosition = targetPosition;
        /** @type {number} */
        this.time = 0;
        /** @type {number} */
        this.mxTime = mxTime;
        /** @type {Function} */
        this.callback = callback;

        /** @type {Vector2} */
        this.startPosition = new Vector2(0, 0);
        /** @type {boolean} */
        this.enabled = enabled;

        this.entity = null;
    }

    Enable (radius, targetPosition, callback, mxTime = 1){
        this.radius = radius;
        this.targetPosition = targetPosition;
        
        this.callback = callback;
        this.mxTime = mxTime;
        this.enabled = true;
        this.time = 0;
    }
    Disable (){
        this.time = 0;
        this.enabled = false;
        this.callback = null;
    }
}

class ImageContourDot {
    /**
     * @param {Number} nextEntityID - next dot in contour' ID
     */
    constructor (nextEntityID){
        this.nextEntityID = nextEntityID;
        this.alphaAcceleration = 2;
        this.alpha = 0;

        this.entity = null;
    }
}

class LineRenderComponent {
    /**
     * @param {Vector2} position
     * @param {Vector3} color
     * @param {Vector3} endColor
     * @param {number} alpha
     */
    constructor (position, color, endColor, alpha = 1){
        this.position = position;
        this.color = color;
        this.endColor = endColor;
        this.alpha = alpha;

        this.entity = null;
    }
}
/**
 * Makes an entity follow an html element
 */
class HTMLfollowComponent {
    
    /**
     * Makes an entity follow an html element
     * @param {HTMLElement | null} htmlElement the element to follow
     * @param {Vector2} offset the normalized offset from top left
     * @param {boolean} enabled is component enabled by default
     */
    constructor (htmlElement, offset=new Vector2(0.5, 0.5), enabled=true){
        /** @type {HTMLElement | null} */
        this.htmlElement = htmlElement;
        /** @type {boolean} */
        this.enabled = enabled;

        /** @type {Vector2} */
        this.offset = offset;

        this.entity = null;
    }
}

/**
 * Makes an entity follow a Transform element
 */
class FollowComponent {
    
    /**
     * Makes an entity follow a Transform element
     * @param {TransformComponent} target the element to follow
     * @param {boolean} enabled is component enabled by default
     */
    constructor (target, enabled=true){
        /** @type {TransformComponent} */
        this.target = target;
        /** @type {boolean} */
        this.enabled = enabled;

        this.entity = null;
    }
}

/**
 * Makes an entity follow an html element
 */
class BombComponent {
    
    /**
     * Makes an entity follow an html element
     * @param {number} radius blow radius
     * @param {boolean} hasDetonated will bomb detonate in next frame
     * @param {boolean} enabled is component enabled by default
     */
    constructor (radius, hasDetonated = false, enabled=true){
        /** @type {number} */
        this.radius = radius;
        /** @type {boolean} */
        this.hasDetonated = false;
        /** @type {boolean} */
        this.enabled = enabled;

        this.entity = null;
    }
}

/**
 * Queue for managing emissions of objects.
 * @typedef {Object} EmissionQueue
 * @property {string} type - Type of emitted objects.
 * @property {number} objNumber - Number of objects to be emitted.
 * @property {number} emissionDelay - Delay between emissions in seconds.
 * @property {number} lastEmittedTime - Timestamp of the last emission.
 * @property {Function} callback - Function to be called after all emissions are completed.
 * @property {Function} callbackEach - Function to be called after each emission is completed.
 * @property {Entity[]} entities - Array of entities associated with this emission queue.
 */

class EmitterComponent {
    /**
     * Creates a new emitter component.
     * @param {boolean} [enabled=true] - Whether the emitter component is enabled.
     */
    constructor (enabled = true) {
        /** @type {boolean} */
        this.enabled = enabled;

        /** @type {EmissionQueue[]} */
        this.queue = [];
        /** @type {EmissionQueue[]} */
        this.canceledQueue = [];

        this.entity = null;
    }

    /**
     * Adds a new emission task to the queue.
     * @param {string} type - Type of emitted objects.
     * @param {number} objNumber - Number of objects to be emitted.
     * @param {number} emissionTime - Duration of emission in seconds.
     * @param {Vector3} color - stroke color
     * @param {Function} callback - Function to be called after all emissions are completed.
     * @param {Function} callbackEach - Function to be called after each emission is completed.
     */
    Emit (type = "dot", tag, objNumber, emissionTime, color, callback, callbackEach) {
        const emissionDelay = objNumber > 0 ? emissionTime / objNumber : 0;

        for (let i = 0; i < this.queue.length; i++){
            if (this.queue[i].tag === tag){
                console.error(`Dublicate emitting request tag ${tag}. Skipping`);
                return;
            }
        }

        this.queue.push({
            type: type,
            tag: tag,
            objNumber: objNumber,
            emissionDelay: emissionDelay,
            lastEmittedTime: -1,
            color: color,
            callback: callback,
            callbackEach: callbackEach,
            entities: [],
        });
    }

    /**
     * Removes an emittment request from queue
     * @param {string} tag tag
     */
    CancelEmit(tag){
        for (let i = 0; i < this.queue.length; i++){
            if (this.queue[i].tag === tag){
                this.canceledQueue.push(this.queue[i]);
                console.log(`Emission request canceled for ${tag}`, this.queue[i]);
                this.queue.splice(i, 1);
                return;
            }
        }
        // console.log(`nothing to cancel in emit queue for ${tag}`);
    }
}

  




class TransformSystem {
    /**
     * selects all the entities acceptable to this system
     * @param {EntityManager} entities - entities
     * @param {HTMLCanvasElement} canvas - canvas
     */
    constructor(entities, canvas, enabled = true) {
        /** @type {Map<number, TransformComponent>} */
        this.entities = entities.findComponentsCache(TransformComponent);
        /** @type {HTMLCanvasElement} */
        this.canvas = canvas;
        /** @type {boolean} */
        this.enabled = enabled;
    }

    entityChanged(entity){
        const transform = entity.getComponent(TransformComponent);
        if (transform !== undefined){
            this.entities.set(entity.id, transform);
        } else{
            this.entities.delete(entity.id);
        }
    }
    
    /**
     * @param {number} deltaTime - time elapsed from last call, in seconds
     */
    update(deltaTime) {
        if (!this.enabled)
            return;

        for (let [id, transform] of this.entities) {
            if (transform === undefined){
                this.entities.delete(id);
                continue;
            }
            if (transform.wrapping && transform.enabled) {
                // Wrap around the canvas edges using modulus operator
                transform.position.x = (transform.position.x % this.canvas.width + this.canvas.width) % this.canvas.width;
                transform.position.y = (transform.position.y % this.canvas.height + this.canvas.height) % this.canvas.height;
            }
        };
    }
}

class FollowSystem {
    /**
     * selects all the entities acceptable to this system
     * @param {EntityManager} entities - entities
     * @param {HTMLCanvasElement} canvas - canvas
     */
    constructor(entities, canvas, enabled = true) {
        /** @type {Map<number, Entity>} */
        this.htmlEntities = entities.findComponentsCache(TransformComponent, HTMLfollowComponent);
        /** @type {Map<number, [TransformComponent, FollowComponent]>} */
        this.entities = entities.findComponentsCache(TransformComponent, FollowComponent);
        /** @type {HTMLCanvasElement} */
        this.canvas = canvas;
        /** @type {boolean} */
        this.enabled = enabled;
    }

    entityChanged(entity){
        const transform = entity.getComponent(TransformComponent);
        const htmlfollow = entity.getComponent(HTMLfollowComponent);
        const follow = entity.getComponent(FollowComponent);
        if (transform !== undefined && htmlfollow !== undefined){
            this.htmlEntities.set(entity.id, [transform, htmlfollow]);
        } else{
            this.htmlEntities.delete(entity.id);
        }

        if (transform !== undefined && follow !== undefined){
            this.entities.set(entity.id, [transform, follow]);
        } else{
            this.entities.delete(entity.id);
        }
    }
    
    /**
     * @param {number} deltaTime - time elapsed from last call, in seconds
     */
    update(deltaTime) {
        if (!this.enabled)
            return;

        for (let [id, [transform, follow]] of this.htmlEntities) {
            if (transform === undefined || follow === undefined){
                this.htmlEntities.delete(id);
                continue;
            }
            if (transform.enabled && follow.enabled) {
                const elemPos = getElementPositionOnCanvas(this.canvas, follow.htmlElement, follow.offset.x, follow.offset.y);
                transform.position.x = elemPos.x * (window.devicePixelRatio);
                transform.position.y = elemPos.y * (window.devicePixelRatio);
                // transform.position.x = elemPos.x * 1;
                // transform.position.y = elemPos.y * 1;
            }
        };

        for (let [id, [transform, follow]] of this.entities) {
            if (transform === undefined || follow === undefined){
                this.entities.delete(id);
                continue;
            }
            if (transform.enabled && follow.enabled) {
                transform.position.set(follow.target.position.x, follow.target.position.y);
            }
        };
    }
}


class BombDetonationSystem {
    /**
     * selects all the entities acceptable to this system
     * @param {EntityManager} entities - entities
     * @param {HTMLCanvasElement} canvas - canvas
     */
    constructor(entities, canvas, renderSystem, enabled = true) {
        /** @type {EntityManager} */
        this.entityManager = entities;
        /** @type {Map<number, BombComponent>} */
        this.entities = entities.findComponents(BombComponent);
        /** @type {HTMLCanvasElement} */
        this.canvas = canvas;
        /** @type {RenderSystem} */
        this.renderSystem = renderSystem;
        /** @type {boolean} */
        this.enabled = enabled;
    }

    entityChanged(entity){
        const bo = entity.getComponent(BombComponent);
        if (bo !== undefined){
            this.entities.set(entity.id, bo);
        } else{
            this.entities.delete(entity.id);
        }
    }
    
    /**
     * @param {number} deltaTime - time elapsed from last call, in seconds
     */
    update(deltaTime) {
        if (!this.enabled)
            return;
        for (let [id, bo] of this.entities) {
            if (bo === undefined){
                this.entities.delete(id);
                continue;
            }
            if (bo.enabled && bo.hasDetonated){
                const bombTransform = bo.entity.getComponent(TransformComponent);
                const bombRb = bo.entity.getComponent(RigidbodyComponent);
                let toDelete = this.entityManager.circle(bombTransform.position, bo.radius);
                toDelete.forEach((entity, idDelete) => {
                    if (idDelete === id || idDelete <= 1)
                        return;
                    // this.entityManager.deleteEntity(entity);
                    // entity.getComponent(CircleRendererComponent).SetDecay(0.5);
                    const entTransform = entity.getComponent(TransformComponent);
                    // entity.getComponent(TransformComponent).position.set(bombTransform.position.x, bombTransform.position.y);
                    let velocity = new Vector2(
                        (entTransform.position.x - bombTransform.position.x) + bombRb.velocity.x,
                        (entTransform.position.y - bombTransform.position.y) + bombRb.velocity.y
                    );
                    velocity = velocity.normalize();
                    velocity.set(
                        velocity.x * Math.abs(bombRb.velocity.x) * getRandomFloatInRange(0.5, 1), 
                        velocity.y * Math.abs(bombRb.velocity.y) * getRandomFloatInRange(0.5, 1)
                    );
                    entity.getComponent(RigidbodyComponent).velocity = velocity;
                    entity.removeComponent(RigidbodyFixPosition);
                    entity.removeComponent(ImageContourDot);
                });

                this.renderSystem.startShake(0.5);
                this.entityManager.deleteEntity(bo.entity);
            }
        }
    }
}

class PhysicsSystem {
    /**
     * selects all the entities acceptable to this system
     * @param {EntityManager} entities - entities
     * @param {HTMLCanvasElement} canvas - canvas
     */
    constructor(entities, canvas, enabled = true) {
        /** @type {Map<number, RigidbodyComponent>} */
        this.rbEntities = entities.findComponentsCache(RigidbodyComponent);
        /** @type {Map<number, GravityComponent>} */
        this.gravEntities = entities.findComponentsCache(GravityComponent);
        /** @type {Map<number, RigidbodyFixPosition>} */
        this.fixPosEntities = entities.findComponentsCache(RigidbodyFixPosition);
        /** @type {HTMLCanvasElement} */
        this.canvas = canvas;


        this.initGravTree();

        /** @type {boolean} */
        this.enabled = enabled;
    }

    entityChanged(entity){
        const rb = entity.getComponent(RigidbodyComponent);
        if (rb !== undefined){
            this.rbEntities.set(entity.id, rb);
        } else{
            this.rbEntities.delete(entity.id);
        }

        const gr = entity.getComponent(GravityComponent);
        if (gr !== undefined){
            this.gravEntities.set(entity.id, gr);
        } else{
            this.gravEntities.delete(entity.id);
        }

        const fixPos = entity.getComponent(RigidbodyFixPosition);
        if (fixPos !== undefined){
            this.fixPosEntities.set(entity.id, fixPos);
        } else{
            this.fixPosEntities.delete(entity.id);
        }
    }

    initGravTree(){
        this.tree = new QuadNode(0, 0, this.canvas.width, this.canvas.height, 0, 0, 0);
    }
  
    
    /**
     * @param {number} fixedDeltaTime - time elapsed from last call, in seconds
     */
    update(fixedDeltaTime) {
        if (!this.enabled)
            return;

        this.handleGravity(fixedDeltaTime);
        this.handleForces(fixedDeltaTime);
    }

    handleGravity(){
        this.initGravTree();
        // console.log(this.tree);

        for (let [gravId, gr] of this.gravEntities) {
            if (gr === undefined) {
                this.gravEntities.delete(gravId);
                continue;
            }

            const grTransform = gr.entity.getComponent(TransformComponent);
            if (gr.enabled) {
                this.pushToTree(this.tree, grTransform.position.x, grTransform.position.y, gr.magnitude);
            }
        }
        let cnt = [0];
        for (let [id, rb] of this.rbEntities) {
            if (rb === undefined || this.gravEntities.has(id)) {
                continue;
            }
            if (rb.enabled && rb.gravitatable) {
                let grForce = new Vector2(0, 0);
                this.getGravityForce(this.tree, rb.transform.position.x, rb.transform.position.y, rb.mass, grForce, cnt);
                rb.AddForce(grForce, 0);
            }
        }
        // console.log(cnt[0], cnt[0] / this.rbEntities.size);
    }

    handleForces(fixedDeltaTime){
        for (let [id, rb] of this.rbEntities) {
            
            if (rb === undefined){
                this.rbEntities.delete(id);
                continue;
            }
            if (rb.enabled) {         
                const transform = rb.transform;
                
                const fixPos = this.fixPosEntities.get(id);
                if (fixPos && fixPos.enabled){
                    rb.accumulatedForce.x += (fixPos.targetPosition.x - transform.position.x) * fixPos.density;
                    rb.accumulatedForce.y += (fixPos.targetPosition.y - transform.position.y) * fixPos.density;
                }
                
                const invMass = 1;
                const dt = fixedDeltaTime;
                const velocity = rb.velocity;
                const accumulatedForce = rb.accumulatedForce;
                const accumulatedImpulse = rb.accumulatedImpulse;

                velocity.x += (accumulatedForce.x * dt * invMass) + (accumulatedImpulse.x * invMass);
                velocity.y += (accumulatedForce.y * dt * invMass) + (accumulatedImpulse.y * invMass);
                
                velocity.x *= rb.damping;
                velocity.y *= rb.damping;
                
                accumulatedForce.set(0, 0);
                accumulatedImpulse.set(0, 0);
                
                transform.position.x += velocity.x * dt;
                transform.position.y += velocity.y * dt;
                // sum3 += performance.now() - stamp;

            }
        }
    }

    /**
     * 
     * @param {QuadNode} node 
     * @param {number} x 
     * @param {number} y 
     * @param {number} magnitude 
     */
    pushToTree(node, x, y, magnitude) {
        if (node.empty) {
            node.magnitude = magnitude;
            node.centerX = x;
            node.centerY = y;
            node.isLeaf = true;
            node.empty = false;
            return;
        }

        if (node.isLeaf && node.width >= 4) {
            // If it's a leaf but not too small to subdivide, subdivide and reinsert the current node's data
            this.subdivide(node);
            let oldX = node.centerX;
            let oldY = node.centerY;
            let oldMagnitude = node.magnitude;
            node.magnitude = 0;  // Reset current node's magnitude as it will be recalculated
            this.pushToTree(node, oldX, oldY, oldMagnitude);
        }

        if (node.width < 4) {
            let oldMass = node.magnitude;
            let newMass = oldMass + magnitude;

            if(newMass !== 0) {
                node.centerX = (node.centerX * oldMass + x * magnitude) / newMass;
                node.centerY = (node.centerY * oldMass + y * magnitude) / newMass;
            } else {
                // When the two cancel out, choose an average position (or some default)
                node.centerX = (node.centerX + x) / 2;
                node.centerY = (node.centerY + y) / 2;
            }
            node.magnitude = newMass;
            return;
        }

        let nodeCenterX = node.x + node.width / 2;
        let nodeCenterY = node.y + node.height / 2;

        // Determine the appropriate quadrant for the point (x, y)
        let quadIndex;
        if (x < nodeCenterX && y < nodeCenterY) {
            quadIndex = 0; // top-left
        } else if (x >= nodeCenterX && y < nodeCenterY) {
            quadIndex = 1; // top-right
        } else if (x < nodeCenterX && y >= nodeCenterY) {
            quadIndex = 2; // bottom-left
        } else {
            quadIndex = 3; // bottom-right
        }

        if (!node.sub[quadIndex]) {
            let newWidth = node.width / 2;
            let newHeight = node.height / 2;
            let newX = quadIndex % 2 === 0 ? node.x : nodeCenterX;
            let newY = quadIndex < 2 ? node.y : nodeCenterY;
            node.sub[quadIndex] = new QuadNode(newX, newY, newWidth, newHeight, 0, 0, -1);
        }

        this.pushToTree(node.sub[quadIndex], x, y, magnitude);

        // Update center of mass and magnitude of current node
        let totalMagnitude = 0;
        let totalX = 0;
        let totalY = 0;
        let nonEmptyFound = false;
        for (let i = 0; i < 4; i++) {
            if (node.sub[i] && !node.sub[i].empty) {
                totalMagnitude += node.sub[i].magnitude;
                totalX += node.sub[i].centerX * node.sub[i].magnitude;
                totalY += node.sub[i].centerY * node.sub[i].magnitude;
                nonEmptyFound = true;
            }
        }
        node.magnitude = totalMagnitude;
        node.empty = !nonEmptyFound; // Mark as non-empty if any child is non-empty.
        if(totalMagnitude !== 0) {
            node.centerX = totalX / totalMagnitude;
            node.centerY = totalY / totalMagnitude;
        } else {
            // If the total cancels out to zero, you might default to the geometric center.
            node.centerX = node.x + node.width / 2;
            node.centerY = node.y + node.height / 2;
        }
    }

    /**
     * Subdivide the current node into 4 subnodes
     * @param {QuadNode} node 
     */
    subdivide(node) {
        let halfWidth = node.width / 2;
        let halfHeight = node.height / 2;
        node.sub[0] = new QuadNode(node.x, node.y, halfWidth, halfHeight, 0, 0, 0);
        // Each new QuadNode’s constructor will set empty = true.
        node.sub[1] = new QuadNode(node.x + halfWidth, node.y, halfWidth, halfHeight, 0, 0, 0);
        node.sub[2] = new QuadNode(node.x, node.y + halfHeight, halfWidth, halfHeight, 0, 0, 0);
        node.sub[3] = new QuadNode(node.x + halfWidth, node.y + halfHeight, halfWidth, halfHeight, 0, 0, 0);
        node.isLeaf = false;
    }

    getGravityForce(node, x, y, mass, result, cnt) {

        // Calculate distance and direction vector from object to node's center of mass
        const dx = node.centerX - x;
        const dy = node.centerY - y;
        const distanceSquared = dx * dx + dy * dy;

        if (distanceSquared === 0) return;
        // cnt[0]++;

        // Barnes-Hut criterion
        const theta = (node.width * node.width) / distanceSquared;
        
        if (node.isLeaf || theta < 0.75*0.75) {
            // Node is either a leaf or sufficiently far away, use node's center of mass
            const forceMagnitude = (mass * node.magnitude) / Math.max(1, (distanceSquared));
            result.x += forceMagnitude * dx;
            result.y += forceMagnitude * dy;
        } else {
            // Node is not a leaf and not sufficiently far away, recurse into child nodes
            for (let i = 0; i < 4; i++) {
                if (node.sub[i] && !node.sub[i].empty) {
                    this.getGravityForce(node.sub[i], x, y, mass, result, cnt);
                }
            }
        }
    }
}

class SpiralAnimationSystem {
    /**
     * selects all the entities acceptable to this system
     * @param {EntityManager} entities - entities
     * @param {HTMLCanvasElement} canvas - canvas
     */
    constructor(entities, canvas, enabled = true) {
        /** @type {Map<number, [SpiralAnimationComponent, RigidbodyComponent]>} */
        this.entities = entities.findComponentsCache(SpiralAnimationComponent, RigidbodyComponent);
        /** @type {HTMLCanvasElement} */
        this.canvas = canvas;
        /** @type {boolean} */
        this.enabled = enabled;

        this.easingCache = new Map();
    }

    entityChanged(entity){
        const sa = entity.getComponent(SpiralAnimationComponent);
        const rb = entity.getComponent(RigidbodyComponent);
        if (sa !== undefined && rb !== undefined){
            this.entities.set(entity.id, [sa, rb]);
        } else{
            this.entities.delete(entity.id);
        }
    }
  
    
    // Precompute the easing function values
    precomputeEasing(mxTime) {
        const cache = [];
        for (let i = 0; i <= 100; i++) {
            const t = i / 100;
            cache.push(this.easeInOutCubic(t));
        }
        this.easingCache.set(mxTime, cache);
    }

    /**
     *  
     * @param {Number} t eases the number using cubic function
     * @returns {Number}
     */
    easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    /**
     * Tries to retrieve cached easing value
     * @param {Number} t 0-1 number to ease
     * @param {Number} mxTime 
     * @returns {Number}
     */
    getEasedTime(t, mxTime) {
        const cache = this.easingCache.get(mxTime);
        if (cache) {
            const index = Math.min(Math.floor(t * 100), 100);
            return cache[index];
        }
        return this.easeInOutCubic(t / mxTime);
    }

    update(deltaTime) {
        if (!this.enabled) return;

        for (let [id, [anim, rb]] of this.entities) {
            if (!anim || !rb) {
                this.entities.delete(id);
                continue;
            }

            if (rb.enabled && anim.enabled) {
                const transform = rb.transform;
                if (anim.time === 0) {
                    anim.startPosition.set(transform.position.x, transform.position.y);
                    if (!this.easingCache.has(anim.mxTime)) {
                        this.precomputeEasing(anim.mxTime);
                    }
                }

                anim.time += deltaTime;
                const easedTime = this.getEasedTime(Math.min(anim.time / anim.mxTime, 1), anim.mxTime);

                let targetPositionX = anim.startPosition.x + anim.radius * easedTime * anim.mxTime * Math.cos(easedTime * anim.mxTime);
                let targetPositionY = anim.startPosition.y + anim.radius * easedTime * anim.mxTime * Math.sin(easedTime * anim.mxTime);

                targetPositionX += Math.min(easedTime, 1) * (anim.targetPosition.x - targetPositionX);
                targetPositionY += Math.min(easedTime, 1) * (anim.targetPosition.y - targetPositionY);

                const forceX = (targetPositionX - transform.position.x) * 140;
                const forceY = (targetPositionY - transform.position.y) * 140;


                // console.log(id);
                if (easedTime >= 1){
                    rb.velocity.set(rb.velocity.x * 0.5, rb.velocity.y * 0.5);
                }
                rb.AddForce(new Vector2(forceX, forceY), 0);


                if (Vector2.Equal(transform.position, anim.targetPosition, 5)) {
                    rb.velocity.set(0, 0);
                    rb.accumulatedForce.set(0, 0);
                    rb.accumulatedImpulse.set(0, 0);
                    transform.position.set(anim.targetPosition.x, anim.targetPosition.y);
                    if (anim.callback) anim.callback();
                    anim.Disable();
                }
            }
        }
    }
}

class RenderSystem {
    /**
     * selects all the entities acceptable to this system
     * @param {EntityManager} entities - entities
     * @param {HTMLCanvasElement} canvas - canvas
     */
    constructor(entities, canvas, enabled = true) {
        this.entityManager = entities;
        /** @type {Map<number, [TransformComponent, CircleRendererComponent]>} */
        this.circleEntities = entities.findComponentsCache(TransformComponent, CircleRendererComponent);
        /** @type {Map<number, [TransformComponent, RoundRecRendererComponent]>} */
        this.rectEntities = entities.findComponentsCache(TransformComponent, RoundRecRendererComponent);
        /** @type {HTMLCanvasElement} */
        this.canvas = canvas;
        /** @type {boolean} */
        this.enabled = enabled;

        this.verticies = new Float32Array(320000);

        this.shakeTime = 0; // Add this line

        this.initializeCanvas();
    }

    entityChanged(entity){
        const tr = entity.getComponent(TransformComponent);
        const cr = entity.getComponent(CircleRendererComponent);
        const rr = entity.getComponent(RoundRecRendererComponent);
        if (tr !== undefined && cr !== undefined){
            this.circleEntities.set(entity.id, [tr, cr]);
        } else{
            this.circleEntities.delete(entity.id);
        }

        if (tr !== undefined && rr !== undefined){
            this.rectEntities.set(entity.id, [tr, rr]);
        } else{
            this.rectEntities.delete(entity.id);
        }
    }

    initializeCanvas() {
        let gl = this.canvas.getContext('webgl', { powerPreference: "high-performance" });
  
        if (!gl) {
            console.warn('WebGL standard context not supported, falling back to experimental context.');
            gl = this.canvas.getContext('experimental-webgl');
        }

        if (!gl) {
            alert('WebGL not supported on this device');
            return null;
        }        

        
        const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
        const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : "Unknown";
        const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : "Unknown";

        console.log("Vendor:", vendor);
        console.log("Renderer:", renderer);


        this.gl = gl;
        
        this.ext = this.gl.getExtension('ANGLE_instanced_arrays');
        if (!this.ext) {
            alert('ANGLE_instanced_arrays not supported on this device');
            return null;
        }


        this.gl.enable(this.gl.BLEND);
        this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);

        this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        
        this.programPoint = this.createProgramPoints();
        this.programLine = this.createProgramLines();
        
        this.initPointShaders();
        this.initLineShaders();

        this.projection = this.createOrthographicMatrix(0, this.canvas.width, this.canvas.height, 0, -1, 1);
        this.initialProjection = this.createOrthographicMatrix(0, this.canvas.width, this.canvas.height, 0, -1, 1);
    }

    initPointShaders(){
        this.positionLocationPoint = this.gl.getAttribLocation(this.programPoint, 'a_position');
        this.sizeLocationPoint = this.gl.getAttribLocation(this.programPoint, 'a_point_size');
        this.colorLocationPoint = this.gl.getAttribLocation(this.programPoint, 'a_color');
        this.projectionLocationPoint = this.gl.getUniformLocation(this.programPoint, 'projection');
        
        this.bufferPoints = this.gl.createBuffer();
    }

    initLineShaders(){
        this.positionLocationLine = this.gl.getAttribLocation(this.programLine, 'position');
        this.pointALocationLine = this.gl.getAttribLocation(this.programLine, 'pointA');
        this.pointBLocationLine = this.gl.getAttribLocation(this.programLine, 'pointB');
        this.colorALocationLine = this.gl.getAttribLocation(this.programLine, 'a_colorA');
        this.colorBLocationLine = this.gl.getAttribLocation(this.programLine, 'a_colorB');
        this.projectionLocationLine = this.gl.getUniformLocation(this.programLine, 'projection');
        

        this.segmentInstanceGeometry = new Float32Array([
        0, -0.5,
        1, -0.5,
        1,  0.5,
        0, -0.5,
        1,  0.5,
        0,  0.5
        ]);

        this.segmentBuffer = this.gl.createBuffer();
        this.pointsBufferLine = this.gl.createBuffer();
    }

    createProgramPoints(){
        const vertexShaderSource = `
            attribute vec2 a_position;
            attribute float a_point_size;
            attribute vec4 a_color;
            uniform mat4 projection;

            varying vec4 v_color; 

            void main() {
                vec4 pos = vec4(a_position, 0, 1);
                gl_Position = projection * pos;
                gl_PointSize = a_point_size;

                v_color = vec4(a_color.rgb / 255.0, a_color.a);
            }
        `;
        const fragmentShaderSource = `
            // Fragment shader
            precision mediump float;
        
            varying vec4 v_color;       // Color passed from the vertex shader
        
            void main() {
                gl_FragColor = v_color;   // Set the fragment color
            }
        `;
    
        return this.createProgram(vertexShaderSource, fragmentShaderSource);
    }

    createProgramLines(){
        const vertexShaderSource = `
            precision highp float;
            attribute vec2 position, pointA, pointB;
            attribute vec4 a_colorA; 
            attribute vec4 a_colorB;

            uniform mat4 projection;

            varying vec4 v_color; 
            
            void main() {
                vec2 xBasis = pointB - pointA;
                vec2 yBasis = normalize(vec2(-xBasis.y, xBasis.x));
                vec2 point = pointA + xBasis * position.x + yBasis * 3.0 * position.y; 
                gl_Position = projection * vec4(point, 0, 1);
        
                // Interpolate between color A and color B based on position.x (0 -> 1 along the line)
                // Normalize colors here (assuming input R,G,B are 0-255)
                vec4 colorA_norm = vec4(a_colorA.rgb / 255.0, a_colorA.a);
                vec4 colorB_norm = vec4(a_colorB.rgb / 255.0, a_colorB.a);
                
                v_color = mix(colorA_norm, colorB_norm, position.x); 
            }
        `;
        const fragmentShaderSource = `
            // Fragment shader
            precision mediump float;
        
            // Receive the interpolated color
            varying vec4 v_color; 

            void main() {
                // Use the interpolated color
                gl_FragColor = v_color; 
            }
        `;
    
        return this.createProgram(vertexShaderSource, fragmentShaderSource);
    }

    createProgram(vertexShaderSource, fragmentShaderSource){
        const vertexShader = this.createShader(this.gl.VERTEX_SHADER, vertexShaderSource);
        const fragmentShader = this.createShader(this.gl.FRAGMENT_SHADER, fragmentShaderSource);
        const program = this.createProgramFromShaders(vertexShader, fragmentShader);
    
        return program;
    }

    createShader(type, source) {
        const shader = this.gl.createShader(type);
        this.gl.shaderSource(shader, source);
        this.gl.compileShader(shader);
        if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
            console.error('Error compiling shader:', this.gl.getShaderInfoLog(shader));
            this.gl.deleteShader(shader);
            return null;
        }
        return shader;
    }

    createProgramFromShaders(vertexShader, fragmentShader) {
        const program = this.gl.createProgram();
        this.gl.attachShader(program, vertexShader);
        this.gl.attachShader(program, fragmentShader);
        this.gl.linkProgram(program);
        if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
            console.error('Error linking program:', this.gl.getProgramInfoLog(program));
            this.gl.deleteProgram(program);
            return null;
        }
        return program;
    }

    renderPoints(verticies, count){
        this.gl.useProgram(this.programPoint);
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.bufferPoints);

        this.gl.enableVertexAttribArray(this.positionLocationPoint);
        this.gl.vertexAttribPointer(this.positionLocationPoint, 2, this.gl.FLOAT, false, 7 * 4, 0);

        this.gl.enableVertexAttribArray(this.colorLocationPoint);
        this.gl.vertexAttribPointer(this.colorLocationPoint, 4, this.gl.FLOAT, false, 7 * 4, 2 * 4);
        
        this.gl.enableVertexAttribArray(this.sizeLocationPoint);
        this.gl.vertexAttribPointer(this.sizeLocationPoint, 1, this.gl.FLOAT, false, 7 * 4, 6 * 4);
    
        this.gl.bufferData(this.gl.ARRAY_BUFFER, verticies, this.gl.DYNAMIC_DRAW);

        this.gl.uniformMatrix4fv(this.projectionLocationPoint, false, this.projection);
        
        this.gl.drawArrays(this.gl.POINTS, 0, count);
    }

    renderLines(verticies, segments){
        // return;
        this.gl.useProgram(this.programLine);

        // --- Setup Base Segment Geometry ---
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.segmentBuffer);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, this.segmentInstanceGeometry, this.gl.STATIC_DRAW);
        
        this.gl.enableVertexAttribArray(this.positionLocationLine);
        this.gl.vertexAttribPointer(this.positionLocationLine, 2, this.gl.FLOAT, false, 0, 0);
        // Base geometry is NOT instanced, so divisor is 0 (default)
        this.ext.vertexAttribDivisorANGLE(this.positionLocationLine, 0); // Explicitly set divisor 0



        // --- Setup Instanced Data (Positions and Colors) ---
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.pointsBufferLine);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, verticies.slice(0, segments * 12), this.gl.DYNAMIC_DRAW); // Use slice just in case

        const stride = Float32Array.BYTES_PER_ELEMENT * 12; // Stride is now 12 floats

        // Point A (Offset 0)
        this.gl.enableVertexAttribArray(this.pointALocationLine);
        this.gl.vertexAttribPointer(this.pointALocationLine, 2, this.gl.FLOAT, false, stride, 0);
        this.ext.vertexAttribDivisorANGLE(this.pointALocationLine, 1);
        
        // Point B (Offset 2 floats)
        this.gl.enableVertexAttribArray(this.pointBLocationLine);
        this.gl.vertexAttribPointer(this.pointBLocationLine, 2, this.gl.FLOAT, false, stride, Float32Array.BYTES_PER_ELEMENT * 2);
        this.ext.vertexAttribDivisorANGLE(this.pointBLocationLine, 1);

        // --- CHANGE 10: Setup TWO color attributes ---
        // Color A (Offset 4 floats)
        this.gl.enableVertexAttribArray(this.colorALocationLine); 
        this.gl.vertexAttribPointer(this.colorALocationLine, 4, this.gl.FLOAT, false, stride, Float32Array.BYTES_PER_ELEMENT * 4); // RGBA
        this.ext.vertexAttribDivisorANGLE(this.colorALocationLine, 1);

        // Color B (Offset 8 floats)
        this.gl.enableVertexAttribArray(this.colorBLocationLine);
        this.gl.vertexAttribPointer(this.colorBLocationLine, 4, this.gl.FLOAT, false, stride, Float32Array.BYTES_PER_ELEMENT * 8); // RGBA
        this.ext.vertexAttribDivisorANGLE(this.colorBLocationLine, 1);

        // --- Draw Instanced ---
        this.gl.uniformMatrix4fv(this.projectionLocationLine, false, this.projection);
        this.ext.drawArraysInstancedANGLE(this.gl.TRIANGLES, 0, this.segmentInstanceGeometry.length / 2, segments);

        // --- CHANGE 11: Reset divisors for ALL instanced attributes ---
        this.ext.vertexAttribDivisorANGLE(this.pointALocationLine, 0);
        this.ext.vertexAttribDivisorANGLE(this.pointBLocationLine, 0);
        this.ext.vertexAttribDivisorANGLE(this.colorALocationLine, 0);
        this.ext.vertexAttribDivisorANGLE(this.colorBLocationLine, 0); // Reset new attribute's divisor too
    }
  
    update(deltaTime) {
        if (!this.enabled)
            return;
        
        let vertexCount = 0;

        // Apply camera shake if shakeTime is active
        if (this.shakeTime > 0) {
            this.shakeTime -= deltaTime;
            const shakeFactor = Math.pow(this.shakeTime / 0.5, 2); // Adjust the exponent for smooth end
            this.setProjectionOffset(
                getRandomFloatInRange(-0.01, 0.01) * shakeFactor,
                getRandomFloatInRange(-0.01, 0.01) * shakeFactor,
                0
            );
        } else {
            this.setProjectionOffset(0, 0, 0); // Reset offset when not shaking
        }

        for (let [id, [transform, circleRenderer]] of this.circleEntities) {
            if (vertexCount + 6 > this.verticies.length)
                break;

            if (!transform || !circleRenderer){
                this.circleEntities.delete(id);
                continue;
            }
            
            if (transform.enabled && circleRenderer.enabled) {

                if (circleRenderer.targetColor !== null){
                    circleRenderer.color.moveTowards(circleRenderer.targetColor, circleRenderer.transitionSpeed * deltaTime);
                    if (Vector3.Equal(circleRenderer.color, circleRenderer.targetColor, 0.1)){
                        circleRenderer.color.x = circleRenderer.targetColor.x;
                        circleRenderer.color.y = circleRenderer.targetColor.y;
                        circleRenderer.color.z = circleRenderer.targetColor.z;
                        circleRenderer.targetColor = null;
                    }
                }
                let alpha = 1;
                if (circleRenderer.totalDecayTime > 0){
                    if (circleRenderer.decayTime > deltaTime){
                        circleRenderer.decayTime -= deltaTime;
                        alpha = circleRenderer.decayTime / circleRenderer.totalDecayTime; 
                    } else{
                        circleRenderer.decayTime = -1;
                        alpha = 0;
                        this.entityManager.deleteEntity(transform.entity);
                        continue;
                    }
                }

                this.verticies[vertexCount++] = transform.position.x;
                this.verticies[vertexCount++] = transform.position.y;
                this.verticies[vertexCount++] = circleRenderer.color.x;
                this.verticies[vertexCount++] = circleRenderer.color.y;
                this.verticies[vertexCount++] = circleRenderer.color.z;
                this.verticies[vertexCount++] = alpha;
                this.verticies[vertexCount++] = circleRenderer.radius;
            }
        }
        
        if (vertexCount > 0){
            this.renderPoints(this.verticies, vertexCount / 7);
        }
    }


    createOrthographicMatrix (left, right, bottom, top, near, far, offsetX = 0, offsetY = 0, offsetZ = 0) {
        const lr = 1 / (left - right);
        const bt = 1 / (bottom - top);
        const nf = 1 / (near - far);
    
        const row1 = -2 * lr;
        const row2 = -2 * bt;
        const row3 = 2 * nf;
    
        const row4 = (left + right) * lr + offsetX;
        const row5 = (top + bottom) * bt + offsetY;
        const row6 = (far + near) * nf + offsetZ;
    
        return new Float32Array([
            row1, 0,    0, 0,
            0, row2,    0, 0,
            0,    0, row3, 0,
            row4, row5, row6, 1
        ]);
    }

    startShake(duration) {
        this.shakeTime = duration;
    }

    setProjectionOffset (offsetX, offsetY, offsetZ){
        this.projection[4 * 3 + 0] = this.initialProjection[12] + offsetX;
        this.projection[4 * 3 + 1] = this.initialProjection[13] + offsetY;
        this.projection[4 * 3 + 2] = this.initialProjection[14] + offsetZ;
    }
}

class DotImageRenderSystem {
    /**
     * selects all the entities acceptable to this system
     * @param {EntityManager} entities - entities
     * @param {RenderSystem} renderSystem - renderer
     */
    constructor(entities, renderSystem, enabled = true) {
        /** @type {Map<number, [SpiralAnimationComponent, ImageContourDot, CircleRendererComponent, TransformComponent]>} */
        this.entities = entities.findComponentsCache(SpiralAnimationComponent, ImageContourDot, CircleRendererComponent, TransformComponent);
        /** @type {Map<number, BombComponent>} */
        this.bombEntities = entities.findComponentsCache(BombComponent);
        /** @type {Map<number, [TransformComponent, LineRenderComponent]>} */
        this.lineEntities = entities.findComponentsCache(TransformComponent, LineRenderComponent);
        /** @type {boolean} */
        this.enabled = enabled;

        this.renderSystem = renderSystem;
        this.verticies = this.renderSystem.verticies;
        this.vertexCount = 0;
    }

    entityChanged(entity){
        const sa = entity.getComponent(SpiralAnimationComponent);
        const ic = entity.getComponent(ImageContourDot);
        const cr = entity.getComponent(CircleRendererComponent);
        const tr = entity.getComponent(TransformComponent);
        const bo = entity.getComponent(BombComponent);
        const lr = entity.getComponent(LineRenderComponent);
        if (sa && ic && cr && tr){
            this.entities.set(entity.id, [sa, ic, cr, tr]);
        } else{
            this.entities.delete(entity.id);
        }

        if (bo){
            this.bombEntities.set(entity.id, bo);
        } else{
            this.bombEntities.delete(entity.id);
        }

        if (tr && lr !== undefined){
            this.lineEntities.set(entity.id, [tr, lr]);
        } else{
            this.lineEntities.delete(entity.id);
        }
    }
  
    update(deltaTime) {
        if (!this.enabled)
            return;
        this.vertexCount = 0;

        this.handleLineEntities();

        for (let [id, [anim, data, renderer, transform1]] of this.entities) {
            if (this.vertexCount + 12 > this.verticies.length)
                break;

            if (!anim || !data || !renderer || !transform1){
                this.entities.delete(id);
                continue;
            }
  
            if (!anim.enabled && renderer.enabled && data) {
                const res = this.entities.get(data.nextEntityID);
                if (!res){
                    continue;
                }
                const [anim2, , renderer2, transform2] = res;
                
                if (!anim2.enabled){
                    data.alpha += data.alphaAcceleration * deltaTime;
                    if (data.alpha > 1)
                        data.alpha = 1;
                    if (data.alpha <= 0){
                        transform1.entity.removeComponent(ImageContourDot);
                        continue;
                    }

                    this.detonateCloseBombs(transform1, transform2);

                    // Position A
                    this.verticies[this.vertexCount++] = transform1.position.x;
                    this.verticies[this.vertexCount++] = transform1.position.y;
                    // Position B
                    this.verticies[this.vertexCount++] = transform2.position.x;
                    this.verticies[this.vertexCount++] = transform2.position.y;
                    // Color A (from renderer1)
                    this.verticies[this.vertexCount++] = renderer.color.x;
                    this.verticies[this.vertexCount++] = renderer.color.y;
                    this.verticies[this.vertexCount++] = renderer.color.z;
                    this.verticies[this.vertexCount++] = data.alpha;
                    // Color B (from renderer2)
                    this.verticies[this.vertexCount++] = renderer2.color.x;
                    this.verticies[this.vertexCount++] = renderer2.color.y;
                    this.verticies[this.vertexCount++] = renderer2.color.z;
                    this.verticies[this.vertexCount++] = data.alpha;
                }
            }
        }
        if (this.vertexCount > 0){
            this.renderSystem.renderLines(this.verticies, this.vertexCount / 12);
        }
    }
    handleLineEntities(){
        for (let [id, [transform, lr]] of this.lineEntities) {
            if (this.vertexCount + 12 > this.verticies.length) break; 
            
            // Position A (Entity's position)
            this.verticies[this.vertexCount++] = transform.position.x;
            this.verticies[this.vertexCount++] = transform.position.y;
            // Position B (Target position)
            this.verticies[this.vertexCount++] = lr.position.x;
            this.verticies[this.vertexCount++] = lr.position.y;
            // Color A (Start Color - Use component's color)
            this.verticies[this.vertexCount++] = lr.color.x;
            this.verticies[this.vertexCount++] = lr.color.y;
            this.verticies[this.vertexCount++] = lr.color.z;
            this.verticies[this.vertexCount++] = lr.alpha;
            // Color B (End Color - Use component's color again, as no endColor is defined)
            this.verticies[this.vertexCount++] = lr.endColor.x;
            this.verticies[this.vertexCount++] = lr.endColor.y;
            this.verticies[this.vertexCount++] = lr.endColor.z;
            this.verticies[this.vertexCount++] = lr.alpha;
        }
    }
    detonateCloseBombs(tr1, tr2){
        for (let [id, bo] of this.bombEntities) {
            if (!bo.enabled || bo.hasDetonated)
                continue;
            // console.log(bo.entity.getComponent(TransformComponent));
            if (Vector2.distanceToLine(bo.entity.getComponent(TransformComponent).position, tr1.position, tr2.position) < 10){
                bo.hasDetonated = true;
            }
        }
    }
}

class EmissionSystem {
    /**
     * selects all the entities acceptable to this system
     * @param {EntityManager} entities - entities
     * @param {HTMLCanvasElement} canvas - canvas
     */
    constructor(entities, canvas, enabled = true) {
        /** @type {EntityManager} */
        this.entityManager = entities;
        /** @type {Map<number, Entity>} */
        this.entities = entities.findComponents(EmitterComponent, RoundRecRendererComponent, TransformComponent);
        /** @type {HTMLCanvasElement} */
        this.canvas = canvas;
        /** @type {boolean} */
        this.enabled = enabled;
    }

    entityChanged(entity){
        const em = entity.getComponent(EmitterComponent);
        const render = entity.getComponent(RoundRecRendererComponent);
        const transform = entity.getComponent(TransformComponent);
        if (em !== undefined && render !== undefined && transform !== undefined){
            this.entities.set(entity.id, entity);
        } else{
            this.entities.delete(entity.id);
        }
    }
  
    
    /**
     * @param {number} deltaTime - time elapsed from last call, in seconds
     */
    update(deltaTime) {
        if (!this.enabled)
            return;
        for (let [id, entity] of this.entities) {
            const emitter = entity.getComponent(EmitterComponent);
            const render = entity.getComponent(RoundRecRendererComponent);
            const emitterTransform = entity.getComponent(TransformComponent);

            if (emitter === undefined || render === undefined || emitterTransform === undefined){
                this.entities.delete(id);
                continue;
            }
            
            if (emitter.enabled) {

                while(emitter.canceledQueue.length > 0){
                    const request = emitter.canceledQueue.pop();
                    
                    request.entities.forEach((entity) => {
                        this.entityManager.deleteEntity(entity);
                    });
                }
                let toRemove = [];
                const currentTime = performance.now();

                for (let i = 0; i < emitter.queue.length; i++){
                    const request = emitter.queue[i];
                    if (request.objNumber <= 0){
                        toRemove.push(i);
                        continue;
                    }
                    if (currentTime - request.lastEmittedTime < request.emissionDelay * 1000)
                        continue;

                    let cnt = Math.floor((currentTime - request.lastEmittedTime) / (request.emissionDelay * 1000));
                    
                    for (let i = 0; i < cnt && i < request.objNumber; i++){
                        const entity = new Entity();
                        if (request.type === "dot" || request.type === "staticDot"){
                            const leftTopBorder = new Vector2(
                                emitterTransform.position.x - (render.width / 2),
                                emitterTransform.position.y - (render.height / 2),
                            );
                            const rightBottomBorder = new Vector2(
                                emitterTransform.position.x + (render.width / 2),
                                emitterTransform.position.y + (render.height / 2),
                            );
                            const position = new Vector2(
                                getRandomFloatInRange(
                                    leftTopBorder.x + render.width * 0.1, 
                                    rightBottomBorder.x - render.width * 0.1
                                ), 
                                getRandomFloatInRange(
                                    leftTopBorder.y + render.height * 0.1, 
                                    rightBottomBorder.y - render.height * 0.1
                                ), 
                            );
                            
                            const velocity = new Vector2((Math.random() - 0.5) * 100, (Math.random() - 0.5) * 100); // Speed in pixels per second
                            
                            const transform = new TransformComponent(position, false);

                            entity.addComponent(transform);
                            entity.addComponent(new RigidbodyComponent(transform, velocity, 1, 0.99));
                            entity.addComponent(new CircleRendererComponent(new Vector3(request.color.x, request.color.y, request.color.z), 3));
                            entity.addComponent(new SpiralAnimationComponent(0, new Vector2(0, 0), 3, null, false));
                        }


                        
                        if (request.type === "staticDot"){
                            entity.getComponent(RigidbodyComponent).velocity = new Vector2(0, 0);
                        }
                        
                        this.entityManager.addEntity(entity);
                        request.entities.push(entity);

                        // console.log(request.tag, entity.id);

                        if (request.callbackEach){
                            request.callbackEach(entity);
                        }
                    }

                    request.objNumber -= cnt;
                    request.lastEmittedTime = currentTime;

                    if (request.objNumber <= 0){
                        toRemove.push(i);
                    }
                }
                
                for (let i = emitter.queue.length - 1; i >= 0; i--){
                    if (i === toRemove[toRemove.length - 1]){
                        emitter.queue[i].callback(emitter.queue[i].entities);
                        emitter.queue.splice(i, 1);
                        toRemove.pop();
                    }
                }
            }
        }
    }
}

export {Entity, EntityManager, TransformComponent, RigidbodyComponent, LineRenderComponent, RigidbodyFixPosition, BombComponent, BombDetonationSystem, FollowComponent, FollowSystem, CircleRendererComponent, EmitterComponent, RoundRecRendererComponent, HTMLfollowComponent, GravityComponent, ImageContourDot, SpiralAnimationComponent, SpiralAnimationSystem, TransformSystem, PhysicsSystem, EmissionSystem, DotImageRenderSystem, RenderSystem};